import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

export function PanelToggleChip({
  open,
  onToggle,
  openIcon,
  closedIcon,
  openLabel,
  closedLabel,
  className = '',
  badgeCount = 0,
  flashToken = 0,
}: {
  open: boolean
  onToggle: () => void
  openIcon: IconDefinition
  closedIcon: IconDefinition
  openLabel: string
  closedLabel: string
  className?: string
  /** Shown only while collapsed — selected entities + listed operations. */
  badgeCount?: number
  /** Bump to softly flash the badge (e.g. after selecting an entity). */
  flashToken?: number
}) {
  const [flashing, setFlashing] = useState(false)

  useEffect(() => {
    if (!flashToken || open || badgeCount <= 0) return
    setFlashing(false)
    const start = window.requestAnimationFrame(() => setFlashing(true))
    const stop = window.setTimeout(() => setFlashing(false), 1200)
    return () => {
      window.cancelAnimationFrame(start)
      window.clearTimeout(stop)
    }
  }, [flashToken, open, badgeCount])

  const showBadge = !open && badgeCount > 0

  return (
    <button
      type="button"
      className={`pointer-events-auto relative badge badge-lg h-9 min-h-0 w-9 cursor-pointer justify-center border-2 p-0 shadow-lg transition-colors duration-200 ${
        open
          ? 'badge-primary border-primary'
          : 'badge-ghost border-base-300 bg-base-100 text-base-content'
      } ${className}`}
      aria-pressed={open}
      aria-expanded={open}
      aria-label={
        open
          ? openLabel
          : showBadge
            ? `${closedLabel} (${badgeCount})`
            : closedLabel
      }
      title={open ? openLabel : closedLabel}
      onClick={onToggle}
    >
      <FontAwesomeIcon
        icon={open ? openIcon : closedIcon}
        className="text-[11px] opacity-90 transition-transform duration-200"
      />
      {showBadge ? (
        <span
          key={`${badgeCount}-${flashToken}`}
          className={`absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-base-100 bg-primary px-1 text-[10px] font-bold leading-none text-primary-content shadow ${
            flashing ? 'control-center-output-badge-flash' : ''
          }`}
          aria-hidden
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      ) : null}
    </button>
  )
}
